import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Users, FileText, CreditCard, Settings, BarChart3, LogOut, Menu, Shield, Sun, Moon, MapPin,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/users", icon: Users, label: "Users" },
  { to: "/admin/profiles", icon: FileText, label: "Profiles" },
  { to: "/admin/attendance", icon: MapPin, label: "Attendance" },
  { to: "/admin/pricing", icon: CreditCard, label: "Pricing" },
  { to: "/admin/settings", icon: Settings, label: "Settings" },
  { to: "/admin/analytics", icon: BarChart3, label: "Analytics" },
];

function AdminSidebarNav({ onItemClick }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-5 border-b border-border">
        <div className="flex items-center gap-2.5">
          <Shield className="text-amber-400" size={22} />
          <span className="font-heading text-lg font-bold tracking-tight">Smart-QR</span>
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px] px-1.5 py-0">ADMIN</Badge>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onItemClick}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-amber-500/10 text-amber-400"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`
            }
            data-testid={`admin-nav-${item.label.toLowerCase()}`}
          >
            <item.icon size={18} strokeWidth={1.8} />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-border">
        <div className="flex items-center justify-between px-3 mb-2">
          <p className="py-1 text-xs text-muted-foreground truncate">{user?.email}</p>
          <button onClick={toggleTheme} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" data-testid="admin-theme-toggle">
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          </button>
        </div>
        <button
          onClick={() => { logout(); navigate("/"); }}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors w-full"
          data-testid="admin-nav-logout"
        >
          <LogOut size={18} strokeWidth={1.8} />
          Logout
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const { user, sessionKey } = useAuth();

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <aside className="hidden md:flex w-60 border-r border-border flex-col shrink-0 bg-sidebar">
        <AdminSidebarNav />
      </aside>
      <div className="md:hidden fixed top-4 left-4 z-50">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9" data-testid="admin-mobile-menu">
              <Menu size={18} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-60 p-0 bg-sidebar">
            <AdminSidebarNav onItemClick={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>
      <main className="flex-1 overflow-auto">
        <div className="p-6 md:p-8 pt-16 md:pt-8 max-w-7xl">
          <Outlet key={`admin-${user?.id || "anon"}-${sessionKey}`} />
        </div>
      </main>
    </div>
  );
}
